import React, { useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Typography,
  Card,
  Space,
  Tooltip,
  message,
  InputNumber,
  Tag,
  Input,
  Radio,
} from "antd";
import { DeleteOutlined } from "@ant-design/icons";

const { Text } = Typography;

// Get color for seal type
const getSealColor = (value) => {
  if (value?.toLowerCase().includes("lab sample seal")) return "blue";
  if (value?.toLowerCase().includes("reference sample seal")) return "pink";
  return "default";
};

// Get hex color for seal type (CSS colors)
const getSealBorderColor = (value) => {
  if (value?.toLowerCase().includes("lab sample seal")) return "#1890ff";
  if (value?.toLowerCase().includes("reference sample seal")) return "#eb2f96";
  return "#d9d9d9";
};

const SealsSection = ({ sealState, updateSealState, sealTypes = [] }) => {
  const { prefix, type, startRange, endRange, manual, seals } = sealState;

  const getSealTypeLabel = (sealTypeId) => {
    const sealType = sealTypes.find((st) => st._id === sealTypeId);
    return sealType?.label || "";
  };

  const getTotalSealCount = () =>
    seals.reduce((total, group) => total + (group.sealNumbers?.length || 0), 0);

  const getSealCountByType = (sealTypeId) => {
    const group = seals.find((g) => g.sealTypeId === sealTypeId);
    return group?.sealNumbers?.length || 0;
  };

  // Check if seal number exists in any group (globally unique)
  const sealNumberExists = (sealNumber) => {
    return seals.some((group) => group.sealNumbers?.includes(sealNumber));
  };

  // Generate seals
  const handleGenerateSeals = () => {
    if (!type) {
      message.error("Please select a seal type");
      return;
    }
    if (
      !startRange ||
      !endRange ||
      isNaN(startRange) ||
      isNaN(endRange) ||
      Number(startRange) < 0 ||
      Number(endRange) < 0
    ) {
      message.error("Please enter valid seal numbers");
      return;
    }
    if (String(startRange).length > 5 || String(endRange).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }
    const start = parseInt(startRange, 10);
    const end = parseInt(endRange, 10);
    if (start > end) {
      message.error("Start should be less than end");
      return;
    }
    if (end - start > 99) {
      message.error("Maximum 100 seals can be generated at a time");
      return;
    }

    // Generate new seal numbers
    const newSealNumbers = [];
    for (let i = start; i <= end; i++) {
      const numericPart = String(i).padStart(5, "0");
      const sealNumber = prefix.trim()
        ? `${prefix}-${numericPart}`
        : numericPart;

      if (!sealNumberExists(sealNumber)) {
        newSealNumbers.push(sealNumber);
      }
    }

    if (newSealNumbers.length === 0) {
      message.info("No new unique seals to add");
      return;
    }

    // Update seals array - find or create group for this type
    const existingGroupIndex = seals.findIndex((g) => g.sealTypeId === type);
    let updatedSeals;

    if (existingGroupIndex >= 0) {
      // Add to existing group
      updatedSeals = seals.map((group, idx) =>
        idx === existingGroupIndex
          ? { ...group, sealNumbers: [...group.sealNumbers, ...newSealNumbers] }
          : group
      );
    } else {
      // Create new group
      updatedSeals = [
        ...seals,
        { sealTypeId: type, sealNumbers: newSealNumbers },
      ];
    }

    updateSealState({ seals: updatedSeals });
  };

  // Add manual seal
  const handleAddManualSeal = () => {
    if (!type) {
      message.error("Please select a seal type");
      return;
    }
    if (!manual || isNaN(manual) || Number(manual) < 0) {
      message.error("Please enter valid seal number");
      return;
    }
    if (String(manual).length > 5) {
      message.error("Maximum 5 digits are allowed");
      return;
    }
    const numericPart = String(manual).padStart(5, "0");
    const sealNumber = prefix.trim() ? `${prefix}-${numericPart}` : numericPart;

    if (sealNumberExists(sealNumber)) {
      message.warning("Seal No. already exists");
      return;
    }

    // Update seals array - find or create group for this type
    const existingGroupIndex = seals.findIndex((g) => g.sealTypeId === type);
    let updatedSeals;

    if (existingGroupIndex >= 0) {
      // Add to existing group
      updatedSeals = seals.map((group, idx) =>
        idx === existingGroupIndex
          ? { ...group, sealNumbers: [...group.sealNumbers, sealNumber] }
          : group
      );
    } else {
      // Create new group
      updatedSeals = [
        ...seals,
        { sealTypeId: type, sealNumbers: [sealNumber] },
      ];
    }

    updateSealState({
      seals: updatedSeals,
      manual: "",
    });
  };

  // Delete seal
  const handleDeleteSeal = (sealTypeId, sealNumber) => {
    const updatedSeals = seals
      .map((group) =>
        group.sealTypeId === sealTypeId
          ? {
              ...group,
              sealNumbers: group.sealNumbers.filter((n) => n !== sealNumber),
            }
          : group
      )
      .filter((group) => group.sealNumbers.length > 0); // Remove empty groups

    updateSealState({ seals: updatedSeals });
  };

  // Handle prefix changes when seals already exist
  useEffect(() => {
    if (seals.length > 0) {
      const updatedSeals = seals.map((group) => ({
        ...group,
        sealNumbers: group.sealNumbers.map((sealNumber) => {
          const parts = sealNumber.split("-");
          const numericPart = parts[parts.length - 1];
          return prefix.trim() ? `${prefix}-${numericPart}` : numericPart;
        }),
      }));
      updateSealState({ seals: updatedSeals });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefix]);

  return (
    <Card
      style={{
        borderRadius: 12,
        border: "1px solid #E5E8EC",
        background: "#fff",
        marginBottom: 16,
      }}
    >
      <Row
        justify="space-between"
        align="middle"
        style={{
          borderBottom: "2px solid #23235F",
          marginBottom: 16,
          paddingBottom: 12,
        }}
      >
        <Text style={{ fontSize: 18, fontWeight: 700, color: "#23235F" }}>
          🔒 Seals
        </Text>
        <Space align="center">
          <Text style={{ fontWeight: 600, color: "#232A36", fontSize: 13 }}>
            Prefix
          </Text>
          <Input
            value={prefix}
            onChange={(e) => updateSealState({ prefix: e.target.value })}
            placeholder="e.g., AG"
            style={{ borderRadius: 8, fontWeight: 600, width: 120 }}
          />
        </Space>
      </Row>

      {/* Seal Type Selection */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 12 }}>
        <Text style={{ fontWeight: 600, color: "#232A36" }}>
          Select Seal Type <span style={{ color: "red" }}>*</span>
        </Text>
        <Radio.Group
          value={type}
          onChange={(e) => updateSealState({ type: e.target.value })}
        >
          <Space size={12}>
            {sealTypes.map((sealType) => (
              <Radio key={sealType._id} value={sealType._id}>
                <Tag
                  color={getSealColor(sealType.label)}
                  style={{ margin: 0, fontWeight: 600 }}
                >
                  {sealType.label}
                </Tag>
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      </Row>

      <Row align="middle" justify="space-between" style={{ marginBottom: 6 }}>
        <Text style={{ fontWeight: 600, color: "#232A36" }}>Seal Range</Text>
        {type && startRange && endRange && endRange >= startRange && (
          <Text style={{ fontSize: 12, color: "#666" }}>
            Will generate{" "}
            <Tag
              color={getSealColor(getSealTypeLabel(type))}
              style={{ margin: "0 4px" }}
            >
              {endRange - startRange + 1} seal
              {endRange - startRange + 1 > 1 ? "s" : ""}
            </Tag>
          </Text>
        )}
      </Row>

      {/* Seal Range */}
      <Row gutter={8} style={{ marginBottom: 16 }}>
        <Col span={9}>
          <InputNumber
            value={startRange}
            onChange={(value) => updateSealState({ startRange: value })}
            placeholder="Start"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={9}>
          <InputNumber
            value={endRange}
            onChange={(value) => updateSealState({ endRange: value })}
            placeholder="End"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Button
            type="primary"
            onClick={handleGenerateSeals}
            style={{
              width: "100%",
              borderRadius: 8,
              fontWeight: 700,
              background: "#23235F",
            }}
          >
            Generate
          </Button>
        </Col>
      </Row>

      {/* Add Manual Seal */}
      <Row align="middle" justify="space-between" style={{ marginBottom: 6 }}>
        <Text style={{ fontWeight: 600, color: "#232A36" }}>
          Add Manual Seal
        </Text>
        {type && manual && (
          <Text style={{ fontSize: 12, color: "#666" }}>
            Will add{" "}
            <Tag
              color={getSealColor(getSealTypeLabel(type))}
              style={{ margin: "0 4px" }}
            >
              {prefix.trim()
                ? `${prefix}-${String(manual).padStart(5, "0")}`
                : String(manual).padStart(5, "0")}{" "}
              (
              {getSealTypeLabel(type)
                ?.replace(" Sample", "")
                .replace(" Seal", "")}
              )
            </Tag>
          </Text>
        )}
      </Row>

      <Row gutter={12} style={{ marginBottom: 15 }}>
        <Col span={18}>
          <InputNumber
            value={manual}
            onChange={(value) => updateSealState({ manual: value })}
            placeholder="Enter Numeric Value (e.g., 1)"
            style={{ borderRadius: 8, fontWeight: 600, width: "100%" }}
          />
        </Col>
        <Col span={6}>
          <Button
            type="primary"
            onClick={handleAddManualSeal}
            style={{
              width: "100%",
              borderRadius: 8,
              fontWeight: 700,
              background: "#23235F",
            }}
          >
            Add
          </Button>
        </Col>
      </Row>

      {/* Seal IDs */}
      {getTotalSealCount() > 0 ? (
        <Card
          style={{
            borderRadius: 15,
            border: "1px solid #E5E8EC",
            background: "#FAFAFA",
          }}
        >
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 12 }}
          >
            <Space>
              <Text style={{ fontWeight: 700, fontSize: 16, color: "#232A36" }}>
                Seal IDs
              </Text>
              {sealTypes.map((sealType) => (
                <Tag
                  key={sealType._id}
                  color={getSealColor(sealType.label)}
                  style={{ fontWeight: 600 }}
                >
                  {sealType.label}: {getSealCountByType(sealType._id)}
                </Tag>
              ))}
            </Space>
            <Tag
              style={{
                fontWeight: 700,
                fontSize: 15,
                background: "#E5E8EC",
                borderRadius: 6,
                padding: "4px 12px",
                border: "none",
              }}
            >
              {getTotalSealCount()}{" "}
              {`seal${getTotalSealCount() > 1 ? "s" : ""}`}
            </Tag>
          </Row>
          <Row
            gutter={[12, 12]}
            style={{
              border: "1px solid #E5E8EC",
              padding: "12px",
              borderRadius: 12,
              overflow: "auto",
              maxHeight: 220,
            }}
          >
            {seals.map((group) =>
              group.sealNumbers.map((sealNumber) => (
                <Col span={8} key={group.sealTypeId + sealNumber}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "#fff",
                      borderRadius: 8,
                      border: `1px solid ${getSealBorderColor(
                        getSealTypeLabel(group.sealTypeId)
                      )}`,
                      padding: "6px 10px",
                      fontWeight: 600,
                      fontSize: 13,
                      color: "#232A36",
                      justifyContent: "space-between",
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <span style={{ fontWeight: 700 }}>{sealNumber}</span>
                      <Tag
                        color={getSealColor(getSealTypeLabel(group.sealTypeId))}
                        style={{ fontSize: 10, padding: "0 4px", margin: 0 }}
                      >
                        {getSealTypeLabel(group.sealTypeId)
                          ?.replace(" Seal", "")
                          .replace(" Sample", "")}
                      </Tag>
                    </div>
                    <Tooltip title="Delete">
                      <Button
                        type="text"
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() =>
                          handleDeleteSeal(group.sealTypeId, sealNumber)
                        }
                        style={{ color: "#E14D4F", fontSize: 14 }}
                      />
                    </Tooltip>
                  </div>
                </Col>
              ))
            )}
          </Row>
        </Card>
      ) : null}
    </Card>
  );
};

export default SealsSection;
